import api, { publicApi } from './axiosConfig';

// Auth API calls
export const auth = {
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  register: async (email, password, username, accessCode) => {
    const response = await api.post('/auth/register', { email, password, username, accessCode });
    return response.data;
  },
  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },
  verifyAccessCode: async (accessCode) => {
    const response = await api.post('/auth/verify-access-code', { accessCode });
    return response.data;
  }
};

// User API calls
export const user = {
  getProfile: async () => {
    const response = await api.get('/users/profile');
    return response.data;
  },
  updateProfile: async (data) => {
    const response = await api.put('/users/profile', data);
    return response.data;
  },
  getUsers: async () => {
    try {
      console.log('[DEBUG] Starting user fetch...');
      // Add timestamp to avoid caching issues
      const timestamp = new Date().getTime();
      console.log('[DEBUG] Calling /users/all endpoint with timestamp:', timestamp);
      
      const response = await api.get('/users/all', { params: { _ts: timestamp } });
      console.log('[DEBUG] Raw response from users/all:', response.data);
      
      // If response is an object with users property, return that array
      if (response.data && Array.isArray(response.data.users)) {
        console.log('[DEBUG] Found users array in response with length:', response.data.users.length);
        return response.data.users;
      }
      
      // If the response is an array directly, return it (fallback for backward compatibility)
      if (Array.isArray(response.data)) {
        console.log('[DEBUG] Response data is an array of length:', response.data.length);
        return response.data;
      }
      
      // No viable array found, log and return empty array
      console.warn('[DEBUG] No viable user data found in response:', response.data);
      return [];
    } catch (error) {
      console.error('[DEBUG] Error in getUsers API call:', error);
      // Try fallback to public endpoint as a last resort
      try {
        console.log('[DEBUG] Attempting fallback to /users/public endpoint');
        const publicResponse = await api.get('/users/public');
        console.log('[DEBUG] Public endpoint response:', publicResponse.data);
        
        if (publicResponse.data && Array.isArray(publicResponse.data.users)) {
          console.log('[DEBUG] Using public users data as last resort');
          return publicResponse.data.users;
        }
      } catch (finalFallbackError) {
        console.error('[DEBUG] Final fallback also failed:', finalFallbackError);
      }
      
      console.log('[DEBUG] All attempts failed, returning empty array');
      return [];
    }
  },
  getPublic: async () => {
    try {
      // Use a public endpoint that doesn't require authentication
      const response = await api.get('/users/public');
      
      if (Array.isArray(response.data)) {
        return { users: response.data };
      } else if (response.data && Array.isArray(response.data.users)) {
        return response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        return { users: response.data.data };
      } else {
        console.warn('Unexpected format for public users data:', response.data);
        return { users: [] };
      }
    } catch (error) {
      // Silently fail for public endpoints
      console.warn('Error fetching public user data:', error);
      return { users: [] };
    }
  },
  getPublicTeamData: async () => {
    try {
      // This endpoint should be accessible without authentication
      // Alternatively, we can create a server endpoint that specifically returns
      // only public user data for the team page
      const response = await api.get('/users/public-team');
      
      // If the endpoint exists and returns properly formatted data
      if (response.data && Array.isArray(response.data.users)) {
        return response.data;
      }
      
      // Fallback to regular getUsers but handle potential auth errors
      try {
        const usersResponse = await api.get('/users/all');
        
        let users = [];
        if (Array.isArray(usersResponse.data)) {
          users = usersResponse.data;
        } else if (Array.isArray(usersResponse.data.users)) {
          users = usersResponse.data.users;
        } else if (usersResponse.data.data && Array.isArray(usersResponse.data.data)) {
          users = usersResponse.data.data;
        }
        
        // Sanitize the data to only include public information
        const sanitizedUsers = users.map(user => ({
          _id: user._id,
          username: user.username,
          name: user.name,
          role: user.role,
          imageUrl: user.imageUrl
        }));
        
        return { users: sanitizedUsers };
      } catch (authError) {
        // If authentication fails, return empty array rather than redirecting
        return { users: [] };
      }
    } catch (error) {
      console.error('Error fetching public team data:', error);
      return { users: [] };
    }
  },
  getChatUsers: async () => {
    const response = await api.get('/users/chat');
    return response.data;
  }
};

// Car API calls
export const car = {
  create: async (data) => {
    const response = await api.post('/cars', data);
    return response.data;
  },
  getUserCars: async () => {
    const response = await api.get('/cars');
    return response.data.cars;
  },
  getAllCars: async () => {
    const response = await api.get('/cars/all');
    return response.data.cars;
  },
  getPublic: async () => {
    try {
      const response = await api.get('/cars/public');
      return response.data || [];
    } catch (error) {
      // Silently fail for public endpoints
      return [];
    }
  },
  getById: async (id) => {
    const response = await api.get(`/cars/${id}`);
    return response.data;
  },
  getUserCarsByUserId: async (userId) => {
    const response = await api.get(`/cars/user/${userId}`);
    return response.data.cars;
  },
  update: async (id, data) => {
    const response = await api.put(`/cars/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/cars/${id}`);
    return response.data;
  },
  getReplacementTypes: async () => {
    const response = await api.get('/cars/replacement-types');
    return response.data.types;
  },
  addReplacement: async (carId, data) => {
    const response = await api.post(`/cars/${carId}/replacements`, data);
    return response.data;
  },
  updateReplacement: async (carId, replacementId, data) => {
    const response = await api.put(`/cars/${carId}/replacements/${replacementId}`, data);
    return response.data;
  },
  deleteReplacement: async (carId, replacementId) => {
    const response = await api.delete(`/cars/${carId}/replacements/${replacementId}`);
    return response.data;
  },
  getRaceHistory: async (carId) => {
    const response = await api.get(`/cars/${carId}/race-history`);
    return response.data;
  }
};

// Task API calls
export const task = {
  create: async (data) => {
    const response = await api.post('/tasks', data);
    return response.data;
  },
  getMyTasks: async (filters = {}) => {
    try {
      const response = await api.get('/tasks/my-tasks', { params: filters });
      return response.data;
    } catch (error) {
      console.error('Error in getMyTasks API call:', error);
      throw error;
    }
  },
  getById: async (id) => {
    const response = await api.get(`/tasks/${id}`);
    return response.data;
  },
  getByCarId: async (carId) => {
    const response = await api.get(`/tasks/car/${carId}`);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/tasks/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/tasks/${id}`);
    return response.data;
  },
  addNote: async (id, content) => {
    const response = await api.post(`/tasks/${id}/notes`, { content });
    return response.data;
  },
  getStats: async () => {
    const response = await api.get('/tasks/stats');
    return response.data;
  },
  getTasksByCarGrouped: async () => {
    try {
      const response = await api.get('/tasks/by-car');
      return response.data;
    } catch (error) {
      console.error('API error in getTasksByCarGrouped:', error);
      throw error;
    }
  },
  calculateLabourCost: async (id) => {
    const response = await api.post(`/tasks/${id}/calculate-labour-cost`);
    return response.data;
  }
};

// Event API calls
export const event = {
  create: async (data) => {
    const response = await api.post('/events', data);
    return response.data;
  },
  getAll: async (filters = {}) => {
    const response = await api.get('/events', { params: filters });
    return response.data;
  },
  getPublic: async (filters = {}) => {
    try {
      const response = await api.get('/events/public', { params: filters });
      // Ensure we're returning an array even if there's an unexpected response format
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      // Silently fail on public endpoints
      console.warn('Error fetching public events:', error);
      return [];
    }
  },
  getById: async (id) => {
    const response = await api.get(`/events/${id}`);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/events/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/events/${id}`);
    return response.data;
  },
  getUserEvents: async () => {
    const response = await api.get('/events/my-events');
    return response.data;
  },
  addParticipant: async (id, data) => {
    const response = await api.post(`/events/${id}/participants`, data);
    return response.data;
  },
  addSpecificParticipant: async (id, data) => {
    const response = await api.post(`/events/${id}/participants/specific`, data);
    return response.data;
  },
  updateParticipantStatus: async (id, data) => {
    const response = await api.put(`/events/${id}/participants/me`, data);
    return response.data;
  },
  updateSpecificParticipantStatus: async (id, userId, data) => {
    const response = await api.put(`/events/${id}/participants/${userId}`, data);
    return response.data;
  },
  updateRaceResults: async (id, data) => {
    const response = await api.put(`/events/${id}/race-results`, data);
    return response.data;
  },
  removeParticipant: async (id) => {
    const response = await api.delete(`/events/${id}/participants/me`);
    return response.data;
  },
  removeSpecificParticipant: async (id, userId) => {
    const response = await api.delete(`/events/${id}/participants/${userId}`);
    return response.data;
  },
  inviteUser: async (id, data) => {
    const response = await api.post(`/events/${id}/invitations`, data);
    return response.data;
  },
  removeInvitedUser: async (id, userId) => {
    const response = await api.delete(`/events/${id}/invitations/${userId}`);
    return response.data;
  },
  updateParticipantTravelDebt: async (id, userId, data) => {
    const response = await api.put(`/events/${id}/participants/${userId}/travel-debt`, data);
    return response.data;
  }
};

// Racetrack API calls
export const racetrack = {
  create: async (data) => {
    const response = await api.post('/racetracks', data);
    return response.data;
  },
  getAll: async () => {
    const response = await api.get('/racetracks');
    return response.data.racetracks;
  },
  getById: async (id) => {
    const response = await api.get(`/racetracks/${id}`);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/racetracks/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/racetracks/${id}`);
    return response.data;
  },
  getByCountry: async (country) => {
    const response = await api.get(`/racetracks/country/${country}`);
    return response.data.racetracks;
  }
};

// Gallery API calls
export const gallery = {
  getAll: async (page = 1, limit = 10) => {
    const response = await api.get('/galleries', { params: { page, limit } });
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/galleries/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/galleries', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/galleries/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/galleries/${id}`);
    return response.data;
  },
  getByEvent: async (eventId) => {
    const response = await api.get(`/galleries/event/${eventId}`);
    return response.data;
  },
  getPublicRandomImage: async () => {
    try {
      const response = await api.get('/galleries/random-image');
      return response.data;
    } catch (error) {
      // Silently fail for public endpoints
      console.warn('Error fetching random gallery image:', error);
      return { url: null };
    }
  }
};

// Admin API calls
export const admin = {
  getStats: async () => {
    const response = await api.get('/admin/stats');
    return response.data;
  },
  deleteUser: async (userId) => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },
  resetUserPassword: async (userId, password) => {
    const response = await api.put(`/admin/users/${userId}/reset-password`, { password });
    return response.data;
  },
  updateUserHourlyPrice: async (userId, hourlyPrice) => {
    const response = await api.put(`/admin/users/${userId}/hourly-price`, { hourlyPrice });
    return response.data;
  },
  deleteCar: async (carId) => {
    const response = await api.delete(`/admin/cars/${carId}`);
    return response.data;
  },
  deleteTask: async (taskId) => {
    const response = await api.delete(`/admin/tasks/${taskId}`);
    return response.data;
  },
  deleteEvent: async (eventId) => {
    const response = await api.delete(`/admin/events/${eventId}`);
    return response.data;
  },
  deleteRacetrack: async (racetrackId) => {
    const response = await api.delete(`/admin/racetracks/${racetrackId}`);
    return response.data;
  },
  deleteReplacement: async (carId, replacementId) => {
    const response = await api.delete(`/admin/cars/${carId}/replacements/${replacementId}`);
    return response.data;
  },
  getAccessCode: async () => {
    const response = await api.get('/admin/access-code');
    return response.data;
  },
  updateAccessCode: async (accessCode) => {
    const response = await api.put('/admin/access-code', { accessCode });
    return response.data;
  },
  getSiteSettings: async () => {
    const response = await api.get('/admin/site-settings');
    return response.data;
  },
  updateSiteSettings: async (data) => {
    const response = await api.put('/admin/site-settings', data);
    return response.data;
  },
  getPublicSiteSettings: async () => {
    try {
      const response = await api.get('/public/site-settings');
      return response.data;
    } catch (error) {
      console.warn('Error fetching public site settings:', error);
      // Return default values as fallback
      return {
        logo: '/logo192.png',
        siteName: 'CAMATS TEAM RACING',
        theme: {
          primaryColor: '#cc3333',
          secondaryColor: '#cc3333',
          backgroundColor: '#000000',
          paperColor: '#121212'
        }
      };
    }
  }
};

// Payments API calls
export const payments = {
  getAllDebts: async (includePaid = false) => {
    const response = await api.get('/payments/debts', { 
      params: { includePaid } 
    });
    return response.data;
  },
  markAsPaid: async (debtId, data) => {
    const response = await api.put(`/payments/debts/${debtId}/paid`, data);
    return response.data;
  }
};

// Notification API calls
export const notification = {
  getAll: async (params = {}) => {
    try {
      console.log('Fetching notifications with params:', params);
      const response = await api.get('/notifications', { params });
      console.log('Notifications API response:', {
        count: response.data.notifications.length,
        types: response.data.notifications.map(n => n.type),
        galleryCount: response.data.notifications.filter(n => n.type === 'gallery').length
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  },
  markAsRead: async (id) => {
    const response = await api.put(`/notifications/${id}/read`);
    return response.data;
  },
  markAllAsRead: async () => {
    const response = await api.put('/notifications/mark-all-read');
    return response.data;
  }
};

// Channel API calls
export const channel = {
  getUserChannels: async () => {
    const response = await api.get('/channels');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/channels/${id}`);
    return response.data;
  },
  create: async (channelData) => {
    const response = await api.post('/channels', channelData);
    return response.data;
  },
  update: async (id, updateData) => {
    const response = await api.put(`/channels/${id}`, updateData);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/channels/${id}`);
    return response.data;
  },
  lockChannel: async (id) => {
    const response = await api.post(`/channels/${id}/lock`);
    return response.data;
  },
  unlockChannel: async (id) => {
    const response = await api.post(`/channels/${id}/unlock`);
    return response.data;
  },
  addMember: async (channelId, userId) => {
    const response = await api.post(`/channels/${channelId}/members`, { userId });
    return response.data;
  },
  removeMember: async (channelId, userId) => {
    const response = await api.delete(`/channels/${channelId}/members/${userId}`);
    return response.data;
  },
  inviteUsers: async (channelId, userIds) => {
    const response = await api.post(`/channels/${channelId}/members/batch`, { userIds });
    return response.data;
  },
  getOrCreateDirectMessage: async (userId) => {
    const response = await api.get(`/channels/direct/${userId}`);
    return response.data;
  },
  updateTeamChannelsMembers: async () => {
    const response = await api.post('/channels/update-team-channels');
    return response.data;
  },
  updateChannelsOrder: async (channelOrders) => {
    const response = await api.post('/channels/update-order', { channelOrders });
    return response.data;
  }
};

// Message API calls
export const message = {
  getChannelMessages: async (channelId, params = {}) => {
    const response = await api.get(`/messages/channel/${channelId}`, { params });
    return response.data;
  },
  create: async (channelId, content) => {
    const response = await api.post(`/messages/channel/${channelId}`, { content });
    return response.data;
  },
  getById: async (messageId) => {
    const response = await api.get(`/messages/${messageId}`);
    return response.data;
  },
  update: async (messageId, content) => {
    const response = await api.put(`/messages/${messageId}`, { content });
    return response.data;
  },
  delete: async (messageId) => {
    const response = await api.delete(`/messages/${messageId}`);
    return response.data;
  },
  markAsRead: async (channelId) => {
    const response = await api.post(`/messages/channel/${channelId}/read`);
    return response.data;
  },
  getUnreadCounts: async (userId, channelIds = null) => {
    let url = `/messages/unread/${userId}`;
    let params = {};
    
    if (channelIds) {
      params.channels = JSON.stringify(channelIds);
    }
    
    const response = await api.get(url, { params });
    return response.data;
  }
};

// Sponsors API calls
export const sponsor = {
  getAll: async () => {
    const response = await api.get('/sponsors');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/sponsors/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/sponsors', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/sponsors/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/sponsors/${id}`);
    return response.data;
  },
  getPublic: async () => {
    try {
      // Use the publicApi instance that doesn't send auth credentials
      const response = await publicApi.get('/sponsors/public');
      return response.data;
    } catch (error) {
      // Silently fail for public endpoints
      return [];
    }
  }
};

// Noticeboard API calls
export const noticeboard = {
  getAll: async () => {
    const response = await api.get('/noticeboard');
    return response.data;
  },
  getById: async (id) => {
    const response = await api.get(`/noticeboard/${id}`);
    return response.data;
  },
  create: async (data) => {
    const response = await api.post('/noticeboard', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await api.put(`/noticeboard/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await api.delete(`/noticeboard/${id}`);
    return response.data;
  },
  markAsRead: async (id) => {
    const response = await api.post(`/noticeboard/${id}/read`);
    // Dispatch an event to notify that a notice was read
    window.dispatchEvent(new CustomEvent('noticeRead'));
    return response.data;
  },
  getUnreadCount: async () => {
    const response = await api.get('/noticeboard/unread-count');
    return response.data;
  },
  markAllAsRead: async () => {
    const response = await api.post('/noticeboard/mark-all-read');
    // Dispatch an event to notify that all notices were read
    window.dispatchEvent(new CustomEvent('noticeRead'));
    return response.data;
  }
};

export default api; 